import { useState } from "react";
import { BadgeCheck, Languages } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Renders a data name translated via tn(), with a provenance badge in non-English locales:
// emerald check = admin-verified (manual), grey languages icon = auto-translated (admins can click to correct).
export function TranslatedName({ text, className = "" }) {
  const { lang, tnInfo, applyOverride } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const info = tnInfo(text);
  const isAdmin = user?.role === "admin";
  const manual = info.state === "manual";
  const machine = info.state === "machine";

  const openEditor = (e) => {
    e.stopPropagation();
    setDraft(info.text);
    setOpen(true);
  };
  const save = async () => {
    const v = draft.trim();
    if (!v || v === info.text) { setOpen(false); return; }
    setSaving(true);
    try {
      await api.post("/i18n/override", { text, lang, translation: v });
      applyOverride(text, v);
      toast.success("Translation saved — this spelling is now locked");
      setOpen(false);
    } catch {
      toast.error("Couldn't save the translation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid={`tn-${text}`}>
      <span>{info.text}</span>
      {manual && (
        <span title="Verified translation" data-testid={`tn-verified-${text}`}>
          <BadgeCheck className="h-3 w-3 text-emerald-500 shrink-0" />
        </span>
      )}
      {machine && isAdmin && (
        <span role="button" tabIndex={0} onClick={openEditor} onKeyDown={(e) => e.key === "Enter" && openEditor(e)} title="Auto-translated — click to correct" data-testid={`tn-review-${text}`} className="shrink-0 cursor-pointer">
          <Languages className="h-3 w-3 text-slate-300 hover:text-blue-500 transition-colors" />
        </span>
      )}
      {manual && isAdmin && (
        <span role="button" tabIndex={0} onClick={openEditor} onKeyDown={(e) => e.key === "Enter" && openEditor(e)} title="Edit verified translation" data-testid={`tn-edit-${text}`} className="shrink-0 -ml-0.5 cursor-pointer">
          <Languages className="h-3 w-3 text-slate-300 hover:text-blue-500 transition-colors" />
        </span>
      )}
      {open && (
        <Dialog open onOpenChange={(v) => !v && setOpen(false)}>
          <DialogContent className="max-w-sm" data-testid="tn-edit-dialog" onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle className="font-display">Correct translation</DialogTitle>
              <DialogDescription className="text-xs font-mono-data">{text} · {lang.toUpperCase()}</DialogDescription>
            </DialogHeader>
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="tn-edit-input" autoFocus />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setOpen(false)} data-testid="tn-edit-cancel">Cancel</Button>
              <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700" disabled={saving} onClick={save} data-testid="tn-edit-save">
                {saving ? "Saving…" : "Save & Lock"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </span>
  );
}
